import { useEffect, useMemo, useState } from 'react';
import { api, resolveMediaUrl } from '../../../apis/api';
import { AdminFileUpload } from '../../components/fileUpload/fileUpload';
import '../orders/orders.css';
import './categories.css';

const emptyForm = { category_name: '', status: 1 };

export const AdminCategories = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getCategoriesWithCounts();
      setRows(res.data || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (c) =>
        String(c.category_id).includes(q) ||
        (c.category_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const resetImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview('');
  };

  const openAdd = () => {
    setModalMode('add');
    setEditing(null);
    setForm(emptyForm);
    resetImage();
  };

  const openEdit = (cat) => {
    setModalMode('edit');
    setEditing(cat);
    setForm({
      category_name: cat.category_name || '',
      status: Number(cat.status) === 0 ? 0 : 1,
    });
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview(cat.category_image ? resolveMediaUrl(cat.category_image) : '');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditing(null);
    setForm(emptyForm);
    resetImage();
  };

  const onImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const save = async () => {
    const category_name = form.category_name.trim();
    if (!category_name) {
      alert('Category name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        category_name,
        status: Number(form.status) === 1 ? 1 : 0,
        imageFile: imageFile || undefined,
      };
      if (modalMode === 'add') {
        await api.createCategory(payload);
      } else if (editing) {
        await api.updateCategory(editing.category_id, payload);
      }
      closeModal();
      await load();
    } catch (err) {
      alert(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="admin-loading">Loading categories…</p>;
  if (error) return <p className="admin-error">{error}</p>;

  return (
    <div className="admin-categories">
      <div className="admin-categories__topbar">
        <input
          className="admin-categories__search"
          type="search"
          placeholder="Filter categories…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Filter categories"
        />
        <div className="admin-categories__topbar-right">
          <span className="admin-categories__count">{filtered.length} categories</span>
          <button type="button" className="admin-btn-primary" onClick={openAdd}>
            Add Category
          </button>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>IMAGE</th>
              <th>NAME</th>
              <th>PRODUCTS</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6}>{rows.length === 0 ? 'No categories yet. Add one to get started.' : 'No matching categories'}</td>
              </tr>
            ) : (
              filtered.map((c) => (
                <tr key={c.category_id}>
                  <td>{c.category_id}</td>
                  <td>
                    {c.category_image ? (
                      <img
                        src={resolveMediaUrl(c.category_image)}
                        alt={c.category_name}
                        className="admin-cat__img"
                      />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{c.category_name}</td>
                  <td>{c.product_count}</td>
                  <td>
                    <span
                      className={`status-badge ${
                        Number(c.status) === 1 ? 'status--active' : 'status--inactive'
                      }`}
                    >
                      {Number(c.status) === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="admin-table__actions">
                    <button type="button" className="admin-btn admin-btn--ghost" onClick={() => openEdit(c)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalMode && (
        <div className="admin-modal-backdrop" onClick={closeModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{modalMode === 'add' ? 'Add Category' : 'Edit Category'}</h3>
            <label>
              Name
              <input
                value={form.category_name}
                onChange={(e) => setForm((f) => ({ ...f, category_name: e.target.value }))}
                placeholder="e.g. SPARKLERS"
                autoFocus
              />
            </label>
            <label>
              Status
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: Number(e.target.value) }))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </label>
            <AdminFileUpload
              id="category-image-upload"
              label="Image"
              accept="image/*"
              fileName={imageFile?.name || ''}
              onChange={onImageChange}
            />
            {imagePreview ? (
              <div className="admin-categories__preview">
                <img src={imagePreview} alt="Preview" />
              </div>
            ) : null}
            <div className="admin-modal__actions">
              <button type="button" className="admin-btn admin-btn--ghost" onClick={closeModal}>
                Cancel
              </button>
              <button type="button" className="admin-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : modalMode === 'add' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
